import { useGetTimetableQuery } from '@schooly/api';
import { Loading, ModalLarge, NotFoundContent } from '@schooly/style';
import { FC, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useRouter } from '../../../../context/router/useRouter';
import { WithTimetable } from '../../../../context/timetable/WithTimetable';
import useSchoolYears from '../../../../hooks/useSchoolYears';
import { TimetableModalContent } from './TimetableModalContent';

export const EditTimetableModal: FC = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams<'id'>();
  const { goBack } = useRouter();
  const { getSchoolYearById, isPreviousSchoolYear } = useSchoolYears();

  const {
    data: timetable,
    isFetching: isFetchingTimetable,
    isLoading: isLoadingTimetable,
  } = useGetTimetableQuery(
    { timetableId: id },
    {
      enabled: !!id,
      refetchOnMount: 'always',
    },
  );

  const schoolYear = useMemo(
    () => (timetable?.school_year_id ? getSchoolYearById(timetable.school_year_id) : undefined),
    [timetable?.school_year_id, getSchoolYearById],
  );

  const handleClose = useCallback(() => navigate('/settings/timetables'), [navigate]);

  const isLoading = isFetchingTimetable || isLoadingTimetable;

  if (isLoading) {
    return (
      <ModalLarge open onClose={handleClose}>
        <Loading />
      </ModalLarge>
    );
  }

  const notFound = !timetable || !schoolYear || isPreviousSchoolYear(schoolYear);

  if (notFound) {
    return (
      <ModalLarge open onClose={handleClose}>
        <NotFoundContent goBack={goBack} />
      </ModalLarge>
    );
  }

  return (
    <WithTimetable timetable={timetable} structure={timetable.structure} schoolYear={schoolYear}>
      <TimetableModalContent onClose={handleClose} mode="edit" />
    </WithTimetable>
  );
};
